import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, money, errMsg } from "@/lib/api";
import { AdminTitle, Btn, Pill, Table } from "@/admin/AdminLayout";
import { RewardsSettings } from "@/admin/RewardsSettings";

function MemberRow({ m, onChanged }: any) {
  const [open, setOpen] = useState(false);
  const [hist, setHist] = useState<any[] | null>(null);
  const [adj, setAdj] = useState({ amount: "", note: "" });
  const toggle = async () => { setOpen(!open); if (!hist) setHist((await api.get(`/admin-x/rewards/${m.id}`)).data); };
  const adjust = async () => {
    try { await api.post("/admin-x/rewards/adjust", { user_id: m.id, amount: Number(adj.amount), note: adj.note }); toast("Balance updated"); setAdj({ amount: "", note: "" }); setHist((await api.get(`/admin-x/rewards/${m.id}`)).data); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <tr data-testid={`vip-member-${m.id}`}>
        <td className="px-4 py-3">{m.name}<div className="text-xs text-neutral-500">{m.email}</div></td>
        <td className="px-4 py-3" data-testid={`vip-member-available-${m.id}`}>{money(m.available)}</td>
        <td className="px-4 py-3">{money(m.pending)}</td>
        <td className="px-4 py-3">{money(m.lifetime)}</td>
        <td className="px-4 py-3 text-xs">{m.last_activity ? new Date(m.last_activity).toLocaleDateString("en-AU") : "—"}</td>
        <td className="px-4 py-3 text-right"><Btn ghost onClick={toggle} data-testid={`vip-member-manage-${m.id}`}>{open ? "Close" : "Manage"}</Btn></td>
      </tr>
      {open && (
        <tr><td colSpan={6} className="px-4 py-4 bg-neutral-50">
          <div className="flex flex-wrap gap-2 items-end mb-4" data-testid="vip-adjust-form">
            <input type="number" step="0.01" placeholder="Amount (A$, use − to deduct)" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} className="border px-2 py-1.5 text-sm w-56" data-testid="vip-adjust-amount" />
            <input placeholder="Reason (shown to the customer)" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} className="border px-2 py-1.5 text-sm flex-1 min-w-[200px]" data-testid="vip-adjust-note" />
            <Btn onClick={adjust} disabled={!adj.amount || adj.note.length < 2} data-testid="vip-adjust-submit">Adjust balance</Btn>
          </div>
          {!hist ? null : hist.length === 0 ? <p className="text-xs text-neutral-500">No activity yet.</p> : (
            <div className="space-y-1 text-xs" data-testid="vip-member-history">
              {hist.map((e) => (
                <div key={e.id} className="flex gap-4"><span className="w-24 text-neutral-500">{new Date(e.created_at).toLocaleDateString("en-AU")}</span><span className="flex-1">{e.label}{e.order_number ? ` · ${e.order_number}` : ""}{e.note ? ` — ${e.note}` : ""}</span>
                  <Pill tone={e.status === "void" ? "red" : e.status === "pending" || e.status === "held" ? "amber" : "green"}>{e.status}</Pill><span className="w-24 text-right">{e.amount < 0 ? "−" : "+"}{money(Math.abs(e.amount))}</span></div>
              ))}
            </div>
          )}
        </td></tr>
      )}
    </>
  );
}

export default function AdminRewards() {
  const [d, setD] = useState<any>(null);
  const [tab, setTab] = useState("members");
  const [q, setQ] = useState("");
  const load = () => api.get("/admin-x/rewards").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const list = d.members.filter((m: any) => !q || `${m.name} ${m.email}`.toLowerCase().includes(q.toLowerCase()));
  const sum = (k: string) => d.members.reduce((a: number, m: any) => a + (m[k] || 0), 0);
  return (
    <div data-testid="admin-rewards">
      <AdminTitle title="Somnio VIP" sub="Rewards program: member balances, manual adjustments and earning rules. Page wording is edited in Pages & Education › Somnio VIP." />
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {[["Outstanding balances", sum("available")], ["Pending (61-day hold)", sum("pending")], ["Lifetime rewards issued", sum("lifetime")]].map(([l, v]: any) => (
          <div key={l} className="bg-white border border-neutral-200 p-5"><p className="eyebrow text-[10px] text-neutral-500">{l}</p><p className="font-display text-3xl mt-2">{money(v)}</p></div>
        ))}
      </div>
      <div className="flex gap-6 mb-6 items-center">
        {[["members", "Members"], ["settings", "Earning rules"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`eyebrow pb-2 border-b ${tab === k ? "border-neutral-900" : "border-transparent text-neutral-500"}`} data-testid={`vip-admin-tab-${k}`}>{l}</button>
        ))}
        <Link to="/pages/rewards" target="_blank" className="ml-auto text-xs underline" data-testid="vip-admin-view-page">View VIP page</Link>
      </div>
      {tab === "members" ? (
        <>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="border border-neutral-300 bg-white px-3 py-2 text-sm mb-4 w-full max-w-sm" data-testid="vip-member-search" />
          <Table head={["Member", "Available", "Pending", "Lifetime", "Last activity", ""]} testId="vip-members-table">
            {list.map((m: any) => <MemberRow key={m.id} m={m} onChanged={load} />)}
          </Table>
        </>
      ) : <RewardsSettings cfg={d.config} onSaved={load} />}
    </div>
  );
}
